import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity, AlertTriangle, Database, FolderKanban, Search, Server, Plane, SlidersHorizontal, BellRing, Radar } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { TYPE_LABELS } from "@/lib/i18n";
import { PageHeader, StatusDot, EpistemicBadge } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const WIDGETS = { activity: "Activité des connecteurs", types: "Recherches par type", recent: "Dernières analyses", alerts: "Alertes récentes", system: "État du système" };

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="panel p-5 relative overflow-hidden fade-up" data-testid={testid}>
      <div className="gabon-bar absolute top-0 left-0 w-full opacity-70" />
      <div className="flex items-center justify-between"><span className="eyebrow">{label}</span><Icon size={16} className="text-primary" /></div>
      <div className="text-4xl font-display font-bold mt-3" data-testid={`${testid}-value`}>{value ?? "—"}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [w, setW] = useState(() => JSON.parse(localStorage.getItem("mdg_widgets") || "null") || Object.fromEntries(Object.keys(WIDGETS).map((k) => [k, true])));
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  useEffect(() => localStorage.setItem("mdg_widgets", JSON.stringify(w)), [w]);
  return (
    <div>
      <PageHeader eyebrow="MADENGI · Centre de commandement" title="Tableau de bord" desc="Vue opérationnelle de l'activité d'investigation, des connecteurs et de l'état de la plateforme.">
        <Link to="/air?radar=gabon" data-testid="dashboard-radar-gabon" className="h-9 px-3 rounded-md border border-emerald-500/50 text-emerald-500 text-sm flex items-center gap-2 hover:bg-emerald-500/10"><Radar size={14} /> Radar Libreville</Link>
        <Popover>
          <PopoverTrigger asChild><button data-testid="dashboard-customize-button" className="h-9 px-3 rounded-md border text-sm flex items-center gap-2 hover:bg-accent"><SlidersHorizontal size={14} /> Personnaliser</button></PopoverTrigger>
          <PopoverContent className="w-72 space-y-3">
            {Object.entries(WIDGETS).map(([k, l]) => (
              <label key={k} className="flex items-center justify-between text-sm">{l}<Switch data-testid={`widget-toggle-${k}`} checked={w[k]} onCheckedChange={(v) => setW({ ...w, [k]: v })} /></label>
            ))}
          </PopoverContent>
        </Popover>
      </PageHeader>
      <div className="grid grid-cols-2 xl:grid-cols-6 gap-4 mb-6">
        <Stat testid="dashboard-stats-card-searches" icon={Search} label="Recherches" value={d?.searches} sub="Requêtes multisources exécutées" />
        <Stat testid="dashboard-stats-card-dossiers" icon={FolderKanban} label="Dossiers actifs" value={d?.active_dossiers} sub="Investigations en cours" />
        <Stat testid="dashboard-stats-card-sources" icon={Database} label="Sources connectées" value={d?.sources_connected} sub={`${d?.connectors ?? "—"} connecteurs actifs`} />
        <Stat testid="dashboard-stats-card-alerts" icon={AlertTriangle} label="Indicateurs de risque" value={d ? d.risk.breaches + d.risk.exposures : null} sub={d ? `${d.risk.breaches} compromissions · ${d.risk.exposures} expositions` : ""} />
        <Link to="/monitor"><Stat testid="dashboard-stats-card-monitor" icon={BellRing} label="Alertes de veille" value={d?.monitor.new_alerts} sub={d ? `${d.monitor.watches} objets surveillés` : ""} /></Link>
        <Stat testid="dashboard-stats-card-air" icon={Plane} label="Positions archivées" value={d?.air_positions} sub="Observations ADS-B réelles" />
      </div>
      <div className="grid xl:grid-cols-3 gap-4">
        {w.activity && (
          <div className="panel p-5 xl:col-span-2" data-testid="dashboard-connector-activity">
            <div className="flex items-center gap-2 mb-4"><Activity size={15} className="text-primary" /><h3 className="font-semibold">Activité des connecteurs</h3></div>
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-xs">
                <thead className="eyebrow text-left"><tr><th className="py-2">Connecteur</th><th>Succès</th><th>Échecs</th><th>Latence moy.</th><th>Résultats</th><th>Dernier appel</th></tr></thead>
                <tbody className="font-mono">
                  {(d?.connector_activity || []).map((a) => (
                    <tr key={a.connector} className="border-t"><td className="py-1.5 flex items-center gap-2"><StatusDot status={a.fail > a.ok ? "error" : "ok"} />{a.connector}</td>
                      <td className="text-emerald-500">{a.ok}</td><td className="text-rose-400">{a.fail}</td><td>{a.avg_ms} ms</td><td>{a.results}</td><td>{fmtDate(a.last)}</td></tr>
                  ))}
                  {d && !d.connector_activity.length && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Aucune activité — lancez une recherche.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {w.system && (
          <div className="panel p-5" data-testid="dashboard-system-status">
            <div className="flex items-center gap-2 mb-4"><Server size={15} className="text-primary" /><h3 className="font-semibold">État du système</h3></div>
            {d && [["API", d.system.api], ["Base de données", d.system.database], ["Stockage objet", d.system.object_storage], ["Moteur IA", d.system.ai_engine]].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between py-2 border-b last:border-0 text-sm"><span>{k}</span><span className="font-mono text-xs flex items-center gap-2"><StatusDot status={v === "error" || v === "indisponible" ? "error" : "ok"} />{v}</span></div>
            ))}
            <div className="text-[10px] font-mono text-muted-foreground mt-3">Horodatage : {fmtDate(d?.system.time)}</div>
          </div>
        )}
        {w.types && (
          <div className="panel p-5" data-testid="dashboard-types-chart">
            <h3 className="font-semibold mb-4">Recherches par type</h3>
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={(d?.by_type || []).map((x) => ({ ...x, label: TYPE_LABELS[x.type] || x.type }))} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" hide /><YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {w.recent && (
          <div className="panel p-5" data-testid="dashboard-recent-searches">
            <h3 className="font-semibold mb-3">Dernières analyses</h3>
            {(d?.recent_searches || []).map((s) => (
              <Link key={s.id} to={`/search?q=${encodeURIComponent(s.query)}`} className="flex items-center justify-between py-2 border-b last:border-0 text-sm hover:text-primary">
                <span className="font-mono truncate">{s.query}</span><span className="text-[10px] text-muted-foreground shrink-0 ml-2">{TYPE_LABELS[s.type]} · {s.result_count}</span>
              </Link>
            ))}
            {(d?.recent_images || []).map((i) => <Link key={i.id} to="/image" className="block py-2 text-xs text-muted-foreground hover:text-primary">Image : {i.filename}</Link>)}
          </div>
        )}
        {w.alerts && (
          <div className="panel p-5" data-testid="dashboard-alerts">
            <h3 className="font-semibold mb-3">Alertes récentes</h3>
            {(d?.alerts || []).map((a) => (
              <div key={a.id} className="py-2 border-b last:border-0"><div className="flex items-center gap-2"><EpistemicBadge value={a.epistemic} /><span className="text-[10px] font-mono text-muted-foreground">{a.query}</span></div><div className="text-xs mt-1">{a.title}</div></div>
            ))}
            {d && !d.alerts.length && <div className="text-xs text-muted-foreground">Aucune compromission ni exposition détectée à ce jour.</div>}
          </div>
        )}
      </div>
    </div>
  );
}
